import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query'
import { apiClient } from '@/shared/lib/api/api-client'
import type { ListRequest, ListResponse, ExportRequest, FilterValue } from '@/shared/lib/api/api-types'

/**
 * Factory that creates a set of TanStack Query hooks for a given REST entity.
 *
 * Convention: /api/{path}/list  (POST)   → ListResponse<TEntity>
 *             /api/{path}/{id}  (GET)    → TEntity
 *             /api/{path}       (POST)   → TEntity (create)
 *             /api/{path}/{id}  (PUT)    → TEntity (update)
 *             /api/{path}/{id}  (DELETE) → void
 *             /api/{path}/export         (POST)   → Blob
 *             /api/{path}/distinct/{field} (GET)  → string[]
 *
 * Usage:
 *   export const bankAccountApi = createCrudApi<BankAccount>('bankAccounts')
 *   const { data } = bankAccountApi.useList(request)
 */
export function createCrudApi<
  TEntity,
  TFilter extends Record<string, FilterValue> = Record<string, FilterValue>,
>(path: string) {
  const base = `/api/${path}` // path = controller name, e.g. 'Accounts' → '/api/Accounts'

  function useList(request: ListRequest<TFilter>) {
    return useQuery({
      queryKey: [path, 'list', request],
      queryFn: ({ signal }) =>
        apiClient.post<ListResponse<TEntity>>(`${base}/list`, request, signal),
      placeholderData: keepPreviousData,
    })
  }

  function useDetail(id: number | string | null) {
    return useQuery({
      queryKey: [path, id],
      queryFn: ({ signal }) => apiClient.get<TEntity>(`${base}/${id!}`, signal),
      enabled: id !== null && id !== 0 && id !== '',
    })
  }

  function useCreate() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (data: unknown) => apiClient.post<TEntity>(base, data),
      onSuccess: () => void qc.invalidateQueries({ queryKey: [path] }),
    })
  }

  function useUpdate() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({ id, data }: { id: number | string; data: unknown }) =>
        apiClient.put<TEntity>(base, { ...(data as object), id }),
      onSuccess: () => void qc.invalidateQueries({ queryKey: [path] }),
    })
  }

  function useDelete() {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (id: number | string) => apiClient.delete(`${base}/${id}`),
      onSuccess: () => void qc.invalidateQueries({ queryKey: [path] }),
    })
  }

  function useDistinct(field: string, enabled = true) {
    return useQuery({
      queryKey: [path, 'distinct', field],
      queryFn: ({ signal }) =>
        apiClient.get<string[]>(`${base}/distinct/${field}`, signal),
      staleTime: 5 * 60 * 1000,
      enabled,
    })
  }

  function useExport() {
    return useMutation({
      mutationFn: (req: ExportRequest<TFilter>) =>
        apiClient.postBlob(`${base}/export`, req),
    })
  }

  return { useList, useDetail, useCreate, useUpdate, useDelete, useDistinct, useExport }
}
